import { CodeArticle as CodeArticleType, CodeArticleVersion, ModelType } from '@livv/models';
import { Box, Button, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { FC, useState } from 'react';
import VersionContent from './VersionContent';
import VersionsDiff from './VersionsDiff';
import VersionsTimeline from './VersionsTimeline';
import { STATUS_DATE_MANAGEMENT } from './VersionsTimeline/const';
import DocumentNotFound from '@components/DocumentNotFound';
import PrintButton from '@components/PrintButton';
import { useBookPageContext } from '@utils/context/book';
import isPdfExportAllowed from '@utils/entitlements/pdfExportPolicy';
import { useBookSheetSlugs } from '@utils/hooks/useBookSlugs';

const filterInvalidVersions = (versions: CodeArticleVersion[]): CodeArticleVersion[] => {
    const nowInSeconds = Date.now() / 1000;

    // one ABROGE case LEGIARTI000006296495 version has invalid start date in future, but end in the past
    return versions.filter(
        ({ dateDebut, dateFin, etat }) =>
            !(dateDebut.seconds > nowInSeconds && dateFin.seconds < nowInSeconds) &&
            etat in STATUS_DATE_MANAGEMENT,
    );
};

const getCurrentVersion = (versions: CodeArticleVersion[]): CodeArticleVersion => {
    const nowInSeconds = Date.now() / 1000;

    return versions.find(({ dateDebut }) => dateDebut.seconds < nowInSeconds) || versions[0];
};

interface CodeArticleProps {
    article: CodeArticleType;
}

const CodeArticle: FC<CodeArticleProps> = ({ article: { title, versions } }) => {
    const validVersions = filterInvalidVersions(versions);
    const [versionsSelected, setVersionsSelected] = useState<CodeArticleVersion[]>([
        getCurrentVersion(validVersions),
    ]);
    const { sheetId: articleId } = useBookSheetSlugs();
    const [comparisonMode, setComparisonMode] = useState(false);
    const { t } = useTranslation('codeArticle');
    const showPrintButton = isPdfExportAllowed(ModelType.LAW_TEXT);

    const handleClick = () => {
        setComparisonMode(!comparisonMode);
        setVersionsSelected(versionsSelected.slice(0, 1));
    };

    return (
        <Box>
            <Box
                sx={{
                    alignItems: 'start',
                    display: 'flex',
                    flexDirection: { sm: 'row', xs: 'column' },
                    gap: 3,
                    justifyContent: 'space-between',
                }}
            >
                <Typography data-test-id={`CodeArticle-${articleId}`} sx={{ flex: 1 }} variant="h2">
                    {title}
                </Typography>
                {showPrintButton && (
                    <PrintButton
                        documentId={articleId}
                        documentName={title}
                        documentType={ModelType.LAW_TEXT}
                        plainButton
                    />
                )}
            </Box>
            <VersionsTimeline
                comparisonMode={comparisonMode}
                setVersionsSelected={setVersionsSelected}
                versions={validVersions}
                versionsSelected={versionsSelected}
            />
            {validVersions.length > 1 && (
                <Button onClick={handleClick} sx={{ mb: 4, mt: 1 }} variant="outlined">
                    {comparisonMode ? t('stopCompareVersions') : t('startCompareVersions')}
                </Button>
            )}
            {comparisonMode ? (
                <Box pt={5}>
                    <VersionsDiff versionsSelected={versionsSelected} />
                </Box>
            ) : (
                <VersionContent
                    articleId={articleId}
                    title={title}
                    versionSelected={versionsSelected[0]}
                />
            )}
        </Box>
    );
};

const CodeArticleWrapper: FC = () => {
    const { sheetData: article } = useBookPageContext();

    if (!article) {
        return <DocumentNotFound />;
    }

    return <CodeArticle article={article as CodeArticleType} />;
};

export default CodeArticleWrapper;
